import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLayoutContext } from '../../layouts/general-layout';
import { LookupService, LookupItem, StoryTypeItem, QuestionItem } from '../../services/lookup/lookup.service';
import { BookService, BookGenerationRequest, GetUserBooksResponse, UserBookInfo } from '../../services/book/book.service';
import { useUserStore } from '../../utils/userStore';
import Button from '../../components/Button';
import BookViewer from '../../components/BookViewer';
import { Loader } from '../../assets/images/index';
import Drawer from '@mui/material/Drawer';
import './playground.scss';
import { Box } from '@mui/material';
import { getApiUrl } from '../../configs/config';
import mockData from './mockData.json';
import { Copy } from '../../assets/images/index';

interface FormData {
  character_name: string;
  character_gender: number;
  character_age: number;
  character_eye_color: string;
  character_has_glass: boolean;
  character_hair_color: string;
  character_hair_style: string;
  illustration_style: string;
  story_type: string;
  story_spirit: string;
  story_tone: string;
  user_display_name: string;
  dedication: string;
  questions_answers: Array<{ question_type_id: number; answer_text: string }>;
  uploaded_image?: File;
  image_provider?: 'flux' | 'nano';
}

interface QuestionAnswer {
  question_type_id: number;
  question_text: string;
  answer_text: string;
}

const Playground: React.FC = () => {
  const { t } = useLayoutContext();

  const [formData, setFormData] = useState<FormData>({
    character_name: '',
    character_gender: 1,
    character_age: 5,
    character_eye_color: '',
    character_has_glass: false,
    character_hair_color: '',
    character_hair_style: '',
    illustration_style: '',
    story_type: '',
    story_spirit: '',
    story_tone: '',
    user_display_name: '',
    dedication: '',
    questions_answers: [],
    uploaded_image: undefined,
    image_provider: undefined
  });

  const [lookupData, setLookupData] = useState({
    eyeColors: [] as LookupItem[],
    hairTypes: [] as LookupItem[],
    hairColors: [] as LookupItem[],
    storyTypes: [] as StoryTypeItem[],
    illustrationStyles: [] as LookupItem[],
    storyTones: [] as LookupItem[],
    storySpirits: [] as LookupItem[],
    questions: [] as QuestionItem[]
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [latestBookId, setLatestBookId] = useState<number | null>(() => {
    const cachedBookId = localStorage.getItem('dreambook_latest_book_id');
    return cachedBookId ? parseInt(cachedBookId) : null;
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [bookContent, setBookContent] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoadingBookContent, setIsLoadingBookContent] = useState(false);
  const [userBooks, setUserBooks] = useState<GetUserBooksResponse>({ books: {} });
  const [isLoadingBooks, setIsLoadingBooks] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const lookupService = useMemo(() => new LookupService(), []);
  const bookService = useMemo(() => new BookService(), []);
  const hasFetchedRef = useRef(false);
  const userInfo = useUserStore((state) => state.userInfo);
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    const fetchLookupData = async () => {
      try {
        setError(null);
        setLoading(true);

        const [
          eyeColors,
          hairTypes,
          hairColors,
          storyTypes,
          illustrationStyles,
          storyTones,
          storySpirits,
          questions
        ] = await Promise.all([
          lookupService.getEyeColors(),
          lookupService.getHairTypes(),
          lookupService.getHairColors(),
          lookupService.getStoryTypes(),
          lookupService.getIllustrationStyles(),
          lookupService.getStoryTones(),
          lookupService.getStorySpirits(),
          lookupService.getActiveQuestions()
        ]);
        
        setLookupData({
          eyeColors,
          hairTypes,
          hairColors,
          storyTypes,
          illustrationStyles,
          storyTones,
          storySpirits,
          questions
        });

        setTimeout(() => {
          if (loading) {
            setLoading(false);
          }
        }, 100);
        
        setLoading(false);
        
      } catch (error) {
        console.error('Error fetching lookup data:', error);
        setError(t('failedToLoadFormData'));
        setLoading(false);
      }
    };

    const fetchUserBooks = async () => {
      try {
        setIsLoadingBooks(true);
        if (userInfo.id) {
          const books = await bookService.getUserBooks(userInfo.id);
          setUserBooks(books);
        }
      } catch (error) {
        console.error('Error fetching user books:', error);
      } finally {
        setIsLoadingBooks(false);
      }
    };

    fetchLookupData();

    if (userInfo.id) {
      fetchUserBooks();
    }

    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError(t('failedToLoadFormData'));
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [lookupService, t, userInfo.id]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      if (field === 'user_display_name') {
        updated.dedication = value ? `${t('enterDedication')} ${value}` : '';
      }
      
      return updated;
    });
  };
  const handleCopyUrl = async (bookUrl: string, key: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(bookUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = bookUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (e) {
      console.error('Failed to copy', e);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert(t('pleaseUploadImageFile'));
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        alert(t('imageFileSizeLimit'));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        uploaded_image: file
      }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      uploaded_image: undefined
    }));
    setImagePreview(null);
  };

  const handleQuestionAnswerChange = (questionTypeId: number, answer: string) => {
    setQuestionAnswers(prev => {
      const existingIndex = prev.findIndex(qa => qa.question_type_id === questionTypeId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { 
          question_type_id: questionTypeId, 
          question_text: lookupData.questions.find(q => q.id === questionTypeId)?.question_text || '',
          answer_text: answer 
        };
        return updated;
      } else {
        return [...prev, { 
          question_type_id: questionTypeId, 
          question_text: lookupData.questions.find(q => q.id === questionTypeId)?.question_text || '',
          answer_text: answer 
        }];
      }
    });
  };

  const resetForm = () => {
    setFormData({
      character_name: '',
      character_gender: 1,
      character_age: 5,
      character_eye_color: '',
      character_has_glass: false,
      character_hair_color: '',
      character_hair_style: '',
      illustration_style: '',
      story_type: '',
      story_spirit: '',
      story_tone: '',
      user_display_name: '',
      dedication: '',
      questions_answers: [],
      uploaded_image: undefined,
      image_provider: undefined
    });
    setQuestionAnswers([]);
    setError(null);
    setImagePreview(null);

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const fillMockData = () => {
    const currentImage = formData.uploaded_image;
    const currentImagePreview = imagePreview;
    
    const computedDedication = `${t('enterDedication')} ${mockData.user_display_name}`;
    
    setFormData({
      character_name: mockData.character_name,
      character_gender: mockData.character_gender,
      character_age: mockData.character_age,
      character_eye_color: mockData.character_eye_color,
      character_has_glass: mockData.character_has_glass,
      character_hair_color: mockData.character_hair_color,
      character_hair_style: mockData.character_hair_style,
      illustration_style: mockData.illustration_style,
      story_type: mockData.story_type,
      story_spirit: mockData.story_spirit,
      story_tone: mockData.story_tone,
      user_display_name: mockData.user_display_name,
      dedication: computedDedication,
      questions_answers: [],
      uploaded_image: currentImage,
      image_provider: undefined
    });

    const mockQuestionAnswers = mockData.questions_answers.map(qa => ({
      question_type_id: qa.question_type_id,
      question_text: lookupData.questions.find(q => q.id === qa.question_type_id)?.question_text || '',
      answer_text: qa.answer_text
    }));

    setQuestionAnswers(mockQuestionAnswers);
    setImagePreview(currentImagePreview);
  };

  const handleGenerateBook = async () => {
    const questionsWithAnswers = questionAnswers.filter(qa => qa.answer_text.trim() !== '');

    const payload: BookGenerationRequest = {
      character_name: formData.character_name,
      character_gender: formData.character_gender,
      character_age: formData.character_age,
      character_eye_color: formData.character_eye_color,
      character_has_glass: formData.character_has_glass,
      character_hair_color: formData.character_hair_color,
      character_hair_style: formData.character_hair_style,
      illustration_style: formData.illustration_style,
      story_type: formData.story_type,
      story_spirit: formData.story_spirit,
      story_tone: formData.story_tone,
      user_display_name: formData.user_display_name,
      dedication: formData.dedication,
      questions_answers: questionsWithAnswers,
      image_provider: formData.image_provider
    };

    try {
      setIsGenerating(true);
      
      setIsDrawerOpen(true);
      setBookContent(null);

      const formDataToSend = new FormData();
      formDataToSend.append('data', JSON.stringify(payload));

      if (formData.uploaded_image) {
        formDataToSend.append('image', formData.uploaded_image);
      }
      
      const response = await bookService.generateBook(formDataToSend);

      setLatestBookId(response.book_id);
      localStorage.setItem('dreambook_latest_book_id', response.book_id.toString());

      try {
        setIsLoadingBookContent(true);
        const bookResponse = await bookService.getBookContent(response.book_id);
        setBookContent(bookResponse);
      } catch (error) {
        console.error('Error fetching book content:', error);
      } finally {
        setIsLoadingBookContent(false);
      }

      try {
        if (userInfo.id) {
          const books = await bookService.getUserBooks(userInfo.id);
          setUserBooks(books);
        }
      } catch (error) {
        console.error('Error refreshing book list:', error);
      }

      resetForm();
      
    } catch (error) {
      console.error('Error generating book:', error);
    } finally {
      setIsGenerating(false);
    }
  };





  if (loading) {
    return (
      <div className="playground-loading">
        <div className="loaderBlock">
          <img src={Loader} alt={t('loading')} />
        </div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="playground">
      {showSuccess && (
        <div className="success-message">
          <span className="badge success">✓</span>
          {t('bookGeneratedSuccessfully')}
        </div>
      )}
      
      <div className="playground-content">
        <div className="form-container">
        <div className="form-header">
          <h1>{t('bookGenerationForm')}</h1>
          <button 
            type="button" 
            className="mock-data-btn"
            onClick={fillMockData}
            title={t('fillFormWithSampleData')}
          >
            {t('fillMockData')}
          </button>
        </div>
        
        <div className="form-section">
          <h2>{t('characterInfo')}</h2>
          
          <div className="flex">
            <div className="form-group flex-1">
              <label>{t('characterName')}:</label>
              <input
                type="text"
                placeholder={t('enterCharacterFullName')}
                value={formData.character_name}
                onChange={(e) => handleInputChange('character_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('characterGender')}:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value={1}
                    checked={formData.character_gender === 1}
                    onChange={(e) => handleInputChange('character_gender', parseInt(e.target.value))}
                  />
                  <span className="badge success">{t('girl')}</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value={2}
                    checked={formData.character_gender === 2}
                    onChange={(e) => handleInputChange('character_gender', parseInt(e.target.value))}
                  />
                  <span className="badge primary">{t('boy')}</span>
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>{t('characterAge')}:</label>
              <select
                value={formData.character_age}
                onChange={(e) => handleInputChange('character_age', parseInt(e.target.value))}
              >
                {Array.from({ length: 100 }, (_, i) => i + 1).map(age => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex">
            <div className="form-group flex-1">
              <label>{t('characterEyeColor')}:</label>
              <select
                value={formData.character_eye_color}
                onChange={(e) => handleInputChange('character_eye_color', e.target.value)}
              >
                <option value="">{t('selectEyeColor')}</option>
                {lookupData.eyeColors.map(color => (
                  <option key={color.id} value={color.name}>{color.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group flex-1">
              <label>{t('characterHasGlasses')}:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="glasses"
                    value="true"
                    checked={formData.character_has_glass === true}
                    onChange={(e) => handleInputChange('character_has_glass', e.target.value === 'true')}
                  />
                  <span className="badge success">{t('yes')}</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="glasses"
                    value="false"
                    checked={formData.character_has_glass === false}
                    onChange={(e) => handleInputChange('character_has_glass', false)}
                  />
                  <span className="badge danger">{t('no')}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex">
            <div className="form-group flex-1">
              <label>{t('characterHairColor')}:</label>
              <select
                value={formData.character_hair_color}
                onChange={(e) => handleInputChange('character_hair_color', e.target.value)}
              >
                <option value="">{t('selectHairColor')}</option>
                {lookupData.hairColors.map(hairColor => (
                  <option key={hairColor.id} value={hairColor.name}>{hairColor.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group flex-1">
              <label>{t('characterHairType')}:</label>
              <select
                value={formData.character_hair_style}
                onChange={(e) => handleInputChange('character_hair_style', e.target.value)}
              >
                <option value="">{t('selectHairType')}</option>
                {lookupData.hairTypes.map(hairType => (
                  <option key={hairType.id} value={hairType.name}>{hairType.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('storyInfo')}</h2>
          
          <div className="flex">
            <div className="form-group flex-1">
              <label>{t('illustrationStyle')}:</label>
              <select
                value={formData.illustration_style}
                onChange={(e) => handleInputChange('illustration_style', e.target.value)}
              >
                <option value="">{t('selectIllustrationStyle')}</option>
                {lookupData.illustrationStyles.map(style => (
                  <option key={style.id} value={style.name}>{style.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group flex-1">
              <label>{t('storyType')}:</label>
              <select
                value={formData.story_type}
                onChange={(e) => handleInputChange('story_type', e.target.value)}
              >
                <option value="">{t('selectStoryType')}</option>
                {lookupData.storyTypes.map(storyType => (
                  <option key={storyType.id} value={storyType.name}>{storyType.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex">
            <div className="form-group flex-1">
              <label>{t('storySpirit')}:</label>
              <select
                value={formData.story_spirit}
                onChange={(e) => handleInputChange('story_spirit', e.target.value)}
              >
                <option value="">{t('selectStorySpirit')}</option>
                {lookupData.storySpirits.map(spirit => (
                  <option key={spirit.id} value={spirit.name}>{spirit.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group flex-1">
              <label>{t('storyTone')}:</label>
              <select
                value={formData.story_tone}
                onChange={(e) => handleInputChange('story_tone', e.target.value)}
              >
                <option value="">{t('selectStoryTone')}</option>
                {lookupData.storyTones.map(tone => (
                  <option key={tone.id} value={tone.name}>{tone.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('userInfo')}</h2>
          
          <div className="form-group">
            <label>{t('userDisplayName')}:</label>
            <input
              type="text"
              placeholder={t('enterYourDisplayName')}
              value={formData.user_display_name}
              onChange={(e) => handleInputChange('user_display_name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('dedication')}:</label>
            <textarea              
              value={formData.dedication}
              placeholder={formData.user_display_name ? `${t('enterDedication')} ${formData.user_display_name}` : `${t('enterDedication')} ${t('enterYourDisplayName')}`}
              onChange={(e) => handleInputChange('dedication', e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>{t('characterImage')}</h2>
          
          <div className="form-group block">
            <label>{t('uploadCharacterImage')}:</label>
            <div className="image-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="image-upload-input"
                id="character-image-upload"
                key={formData.uploaded_image ? 'has-image' : 'no-image'}
              />
              <label htmlFor="character-image-upload" className="image-upload-label">
                <span className="upload-icon">📷</span>
                <span className="upload-text">{t('chooseImage')}</span>
              </label>
              
              {imagePreview && (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Character preview" className="image-preview" />
                  <button type="button" onClick={removeImage} className="remove-image-btn">
                    ✕
                  </button>
                </div>
              )}
            </div>
            <p className="image-upload-help">
              {t('imageUploadHelp')} {t('imageFormatInfo')}
            </p>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('infoQuestions')}</h2>
          
          {lookupData.questions.map((question, index) => (
            <div key={question.id} className="form-group block">
              <label>{t('question')} #{index + 1} - {question.question_text}:</label>
              <textarea
                placeholder={t('enterAnswer')}
                rows={3}
                value={questionAnswers.find(qa => qa.question_type_id === question.id)?.answer_text || ''}
                onChange={(e) => handleQuestionAnswerChange(question.id, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="form-section">
          <div className="generate-section">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.image_provider === 'flux'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleInputChange('image_provider', 'flux');
                    } else {
                      handleInputChange('image_provider', undefined);
                    }
                  }}
                  className="checkbox-input"
                  disabled={isGenerating}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t('realAiImagesFluxModel')}</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.image_provider === 'nano'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleInputChange('image_provider', 'nano');
                    } else {
                      handleInputChange('image_provider', undefined);
                    }
                  }}
                  className="checkbox-input"
                  disabled={isGenerating}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t('realAiImagesNanoBananaModel')}</span>
              </label>
            </div>
            
            <Button
              text={t('generateBook')}
              color="blue"
              size="lg"
              onClick={handleGenerateBook}
              loader={isGenerating}
              disabled={isGenerating}
            />
          </div>
        </div>
        </div>
        <div className="book-list-sidebar">
          <h2>{t('bookList')}</h2>
          <div className="book-list">
            {isLoadingBooks ? (
              <div className="book-item">
                <span className="book-link">{t('loading')}...</span>
              </div>
            ) : Object.keys(userBooks.books).length === 0 ? (
              <div className="book-item">
                <span className="book-link">{t('noBooksYet')}</span>
              </div>
            ) : (
              Object.entries(userBooks.books).map(([bookKey, book]) => (
                <div key={bookKey} className="book-item">
                  <div className="book-row">
                    <a 
                      href={book.url}
                      className="book-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {book.name}
                    </a>
                    <button
                      type="button"
                      className="copy-btn"
                      aria-label={t('copyLink')}
                      title={t('copyLink')}
                      onClick={() => handleCopyUrl(book.url, bookKey)}
                    >
                      <img src={Copy} alt="Copy" className="copy-icon" />
                    </button>
                    {copiedKey === bookKey && (
                      <span className="copied-hint" role="status" aria-live="polite">{t('copied')}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Drawer anchor="right" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} PaperProps={{
        className: "custom-drawer-paper"
      }}>
        <Box className="drawer-content">
          <BookViewer 
            bookContent={bookContent}
            isLoading={isLoadingBookContent}
            isGenerating={isGenerating}
            showHeader={false}
            className=""
            closeButton={
              <button
                className="close-btn"
                onClick={() => setIsDrawerOpen(false)}
              >
                ×
              </button>
            }
          />
        </Box>
      </Drawer>
    </div>
  );
};

export default Playground;
